import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useRef,
  useEffect,
} from 'react';
import { TransportCloudParams } from 'api/transportCloud/transportCloud.types';
import { setGlobalTransportCloudLeave } from 'contexts/authContext/AuthContext';
import { DEFAULT_PARAMS } from './transportCloudContext.const';

type WebSocketClient = any;

const FILTERS_STORAGE_KEY = 'transportCloud_filters';

interface StoredFiltersData {
  searchValue: string;
  checkedOnlyMine: boolean;
  loadingDeliveryDateFrom: string;
  loadingDeliveryDateTo: string;
  savedAt: number;
}

const isFilterExpired = (savedAt: number): boolean => {
  const now = new Date();
  const cutoff = new Date();
  cutoff.setHours(4, 0, 0, 0);
  if (now < cutoff) {
    cutoff.setDate(cutoff.getDate() - 1);
  }
  return savedAt < cutoff.getTime();
};

const loadFiltersFromStorage = (): Omit<
  StoredFiltersData,
  'savedAt'
> | null => {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const stored: StoredFiltersData = JSON.parse(raw);
    if (isFilterExpired(stored.savedAt)) return null;
    return {
      searchValue: stored.searchValue,
      checkedOnlyMine: stored.checkedOnlyMine,
      loadingDeliveryDateFrom: stored.loadingDeliveryDateFrom,
      loadingDeliveryDateTo: stored.loadingDeliveryDateTo,
    };
  } catch {
    return null;
  }
};

const saveFiltersToStorage = (filters: {
  searchValue: string;
  checkedOnlyMine: boolean | null;
  loadingDeliveryDateFrom: string;
  loadingDeliveryDateTo: string;
}): void => {
  if (filters.checkedOnlyMine === null) return;
  try {
    const toStore: StoredFiltersData = {
      searchValue: filters.searchValue,
      checkedOnlyMine: filters.checkedOnlyMine,
      loadingDeliveryDateFrom: filters.loadingDeliveryDateFrom,
      loadingDeliveryDateTo: filters.loadingDeliveryDateTo,
      savedAt: Date.now(),
    };
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toStore));
  } catch {}
};

type CellUpdateHandler = (message: {
  type: string;
  rowId?: number;
  fieldName?: string;
  newValue?: any;
  error?: string;
  errorCode?: string;
  jobEntryIds?: number[];
  shipmentPlanContainerIds?: number[];
}) => void;

type CleanupEditingHandler = () => void;

interface LockedCell {
  rowId: number;
  fieldName: string;
  user: string;
}

export const useProvideTransportCloudContext = () => {
  const [params, setParams] = useState<TransportCloudParams>(DEFAULT_PARAMS);
  const [wsClient, setWsClient] = useState<WebSocketClient | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [activeUsers, setActiveUsers] = useState<string[]>([]);
  const [lockedCells, setLockedCells] = useState<LockedCell[]>([]);
  const [filterState, setFilterState] = useState<{
    searchValue: string;
    checkedOnlyMine: boolean | null;
    loadingDeliveryDateFrom: string;
    loadingDeliveryDateTo: string;
  }>(() => {
    const stored = loadFiltersFromStorage();
    return {
      searchValue: stored?.searchValue ?? '',
      checkedOnlyMine: stored?.checkedOnlyMine ?? null,
      loadingDeliveryDateFrom: stored?.loadingDeliveryDateFrom ?? '',
      loadingDeliveryDateTo: stored?.loadingDeliveryDateTo ?? '',
    };
  });
  const cellUpdateHandlerRef = useRef<CellUpdateHandler | null>(null);
  const cleanupEditingHandlerRef = useRef<CleanupEditingHandler | null>(null);

  useEffect(() => {
    saveFiltersToStorage(filterState);
  }, [filterState]);

  const getTabParams = () => params;

  const getSetTabParams = () => setParams;

  const setWebSocketClient = (client: WebSocketClient | null) => {
    setWsClient(client);
  };

  const registerCellUpdateHandler = (handler: CellUpdateHandler | null) => {
    cellUpdateHandlerRef.current = handler;
  };

  const registerCleanupEditingHandler = (
    handler: CleanupEditingHandler | null,
  ) => {
    cleanupEditingHandlerRef.current = handler;
  };

  const callCleanupEditing = () => {
    if (cleanupEditingHandlerRef.current) {
      cleanupEditingHandlerRef.current();
    }
  };

  const notifyCellUpdate = (message: {
    type: string;
    rowId?: number;
    fieldName?: string;
    newValue?: any;
    error?: string;
    errorCode?: string;
    jobEntryIds?: number[];
    shipmentPlanContainerIds?: number[];
  }) => {
    if (cellUpdateHandlerRef.current) {
      cellUpdateHandlerRef.current(message);
    }
  };

  const sendCellUpdate = (payload: {
    rowId?: number;
    rowIds?: number[];
    shipmentPlanContainerId?: number;
    shipmentPlanContainerIds?: number[];
    fieldName?: string;
    newValue?: any;
    fields?: Record<string, any>;
  }) => {
    if (!wsClient || !wsClient.connected) {
      return false;
    }

    try {
      const destination =
        payload.fields != null
          ? '/app/transport-cloud/update-cells'
          : '/app/transport-cloud/update-cell';
      const message = JSON.stringify({ ...payload });

      wsClient.send(destination, message);
      return true;
    } catch (error) {
      return false;
    }
  };

  const sendCellUpdateNotify = (payload: {
    rowId?: number;
    rowIds?: number[];
    shipmentPlanContainerId?: number;
    shipmentPlanContainerIds?: number[];
    fieldName?: string;
    newValue?: any;
  }) => {
    if (!wsClient || !wsClient.connected) {
      return false;
    }

    try {
      wsClient.send(
        '/app/transport-cloud/update-notify-cell',
        JSON.stringify({ ...payload }),
      );
      return true;
    } catch (error) {
      return false;
    }
  };

  const sendStartEditing = (payload: {
    rowId: number;
    jobId?: number;
    fieldName: string;
  }) => {
    if (!wsClient || !wsClient.connected) {
      return false;
    }

    try {
      const destination = '/app/transport-cloud/start-editing';
      const message = JSON.stringify(payload);

      wsClient.send(destination, message);
      return true;
    } catch (error) {
      return false;
    }
  };

  const sendStopEditing = (payload: {
    rowId: number;
    jobId?: number;
    fieldName: string;
  }) => {
    if (!wsClient || !wsClient.connected) {
      return false;
    }

    try {
      const destination = '/app/transport-cloud/stop-editing';
      const message = JSON.stringify(payload);

      wsClient.send(destination, message);
      return true;
    } catch (error) {
      return false;
    }
  };

  const sendLeaveMessage = () => {
    if (!wsClient || !wsClient.connected) {
      return false;
    }

    try {
      wsClient.send('/app/transport-cloud/leave', JSON.stringify({}));
      return true;
    } catch (error) {
      return false;
    }
  };

  const sendDeleteRows = (payload: {
    jobEntryIds: number[];
    shipmentPlanContainerIds: number[];
  }) => {
    if (!wsClient || !wsClient.connected) {
      return false;
    }

    try {
      const destination = '/app/transport-cloud/delete-rows';
      const message = JSON.stringify(payload);

      wsClient.send(destination, message);
      return true;
    } catch (error) {
      return false;
    }
  };

  return {
    getTabParams,
    getSetTabParams,
    setWebSocketClient,
    sendCellUpdate,
    sendCellUpdateNotify,
    sendStartEditing,
    sendStopEditing,
    sendLeaveMessage,
    sendDeleteRows,
    registerCellUpdateHandler,
    registerCleanupEditingHandler,
    callCleanupEditing,
    notifyCellUpdate,
    wsClient,
    isConnected,
    setIsConnected,
    activeUsers,
    setActiveUsers,
    lockedCells,
    setLockedCells,
    filterState,
    setFilterState,
  };
};

const transportCloudContext = createContext<
  ReturnType<typeof useProvideTransportCloudContext> | undefined
>(undefined);

export const useTransportCloudContext = () => {
  const context = useContext(transportCloudContext);

  if (context === undefined) {
    throw new Error(
      'useTransportCloudContext must be used within a TransportCloudProvider',
    );
  }

  return context;
};

export const TransportCloudContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const auth = useProvideTransportCloudContext();

  useEffect(() => {
    setGlobalTransportCloudLeave(auth.sendLeaveMessage);

    return () => {
      setGlobalTransportCloudLeave(null);
    };
  }, [auth.sendLeaveMessage]);

  return (
    <transportCloudContext.Provider value={auth}>
      {children}
    </transportCloudContext.Provider>
  );
};
